import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/domain/entities';
import { authApi, userApi, setTokens, clearTokens } from '@/infrastructure/services/api-client';

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
}

interface AuthActions {
    login: (email: string, password: string) => Promise<void>;
    register: (name: string, email: string, password: string, options?: { country?: string; acceptTerms?: boolean }) => Promise<void>;
    logout: () => void;
    updateUser: (updates: Partial<User>) => void;
    refreshProfile: () => Promise<void>;
    setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
    persist(
        (set) => ({
            user: null,
            isAuthenticated: false,
            isLoading: false,

            login: async (email: string, password: string) => {
                set({ isLoading: true });
                try {
                    const result = await authApi.login({ email, password });
                    setTokens(result.tokens.accessToken, result.tokens.refreshToken);
                    set({
                        user: {
                            id: result.user.id,
                            name: result.user.name,
                            email: result.user.email,
                            country: result.user.country,
                            role: result.user.role,
                            status: result.user.status,
                            sapConfig: result.user.sapConfig,
                            subscriptions: result.user.subscriptions,
                            adminGrantedPlans: result.user.adminGrantedPlans ?? [],
                            createdAt: new Date(result.user.createdAt),
                        },
                        isAuthenticated: true,
                        isLoading: false,
                    });
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            register: async (name: string, email: string, password: string, options?: { country?: string; acceptTerms?: boolean }) => {
                set({ isLoading: true });
                try {
                    const { acceptTerms, ...address } = options ?? {};
                    const result = await authApi.register({ name, email, password, ...address, acceptTerms: acceptTerms ?? false });
                    setTokens(result.tokens.accessToken, result.tokens.refreshToken);
                    set({
                        user: {
                            id: result.user.id,
                            name: result.user.name,
                            email: result.user.email,
                            country: result.user.country,
                            role: result.user.role,
                            status: result.user.status,
                            sapConfig: result.user.sapConfig,
                            subscriptions: [],
                            adminGrantedPlans: [],
                            createdAt: new Date(result.user.createdAt),
                        },
                        isAuthenticated: true,
                        isLoading: false,
                    });
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            logout: () => {
                clearTokens();
                set({ user: null, isAuthenticated: false });
            },

            updateUser: (updates: Partial<User>) => {
                set((state) => ({
                    user: state.user ? { ...state.user, ...updates } : null,
                }));
            },

            refreshProfile: async () => {
                try {
                    const result = await userApi.getProfile();
                    set({
                        user: {
                            id: result.user.id,
                            name: result.user.name,
                            email: result.user.email,
                            country: result.user.country,
                            role: result.user.role,
                            status: result.user.status,
                            sapConfig: result.user.sapConfig,
                            subscriptions: result.user.subscriptions,
                            adminGrantedPlans: result.user.adminGrantedPlans ?? [],
                            createdAt: new Date(result.user.createdAt),
                        },
                    });
                } catch {
                    // If profile refresh fails, log out
                    clearTokens();
                    set({ user: null, isAuthenticated: false });
                }
            },

            setLoading: (isLoading: boolean) => {
                set({ isLoading });
            },
        }),
        {
            name: 'arcanum-auth',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
        },
    ),
);

export default useAuthStore;
